// Language handoff from the marketing site. A visitor on lumavista.eu/de who
// clicks "Sign up" lands on app.lumavista.eu/auth/register?lang=de; this reads
// that param so the auth pages open in the same language they were browsing in.
//
// Scope (deliberate):
//  - Applies ONLY when the user has no explicit ui_language preference ('auto').
//    An existing user who picked a UI language always wins (see useMessageLanguage).
//  - Persisted in sessionStorage so it survives the register↔login cross-links
//    (which don't carry the query string) within the same tab, then evaporates
//    when the tab closes — it's a session hint, not a stored preference.
//  - SSR / static-export prerender safe (returns null without `window`), so it
//    never perturbs the English-first hydration invariant (I18nProvider §B2).

import { SUPPORTED_MESSAGE_LOCALES, type MessageLocale } from './messageLocale';

const SESSION_KEY = 'lvdr:incoming-lang';
// Accept a few common spellings so the site side isn't locked to one name.
const PARAM_KEYS = ['lang', 'locale', 'hl'] as const;

/** Language-prefix match against the supported catalogs; null if unsupported. */
function toMessageLocale(raw: string | null | undefined): MessageLocale | null {
  if (!raw) return null;
  const lang = raw.toLowerCase().split(/[-_]/)[0] ?? '';
  return (SUPPORTED_MESSAGE_LOCALES as readonly string[]).includes(lang)
    ? (lang as MessageLocale)
    : null;
}

function readParam(): MessageLocale | null {
  try {
    const params = new URLSearchParams(window.location.search);
    for (const key of PARAM_KEYS) {
      const hit = toMessageLocale(params.get(key));
      if (hit) return hit;
    }
  } catch {
    // Malformed URL / SecurityError — treat as no handoff.
  }
  return null;
}

/**
 * The incoming handoff locale, or null. Read-only: prefers the current URL's
 * `?lang=` (the page you actually landed on), then the persisted value from an
 * earlier page in this tab's flow. Call `captureIncomingLocale()` from an effect
 * to persist a fresh param for later pages.
 */
export function getIncomingMessageLocale(): MessageLocale | null {
  if (typeof window === 'undefined') return null;
  const fromUrl = readParam();
  if (fromUrl) return fromUrl;
  try {
    return toMessageLocale(window.sessionStorage.getItem(SESSION_KEY));
  } catch {
    return null;
  }
}

/**
 * Persist a fresh `?lang=` param to sessionStorage so the choice survives
 * subsequent auth-flow navigations that drop the query string. Side-effecting —
 * call from a mount effect, never during render.
 */
export function captureIncomingLocale(): void {
  if (typeof window === 'undefined') return;
  const fromUrl = readParam();
  if (!fromUrl) return;
  try {
    window.sessionStorage.setItem(SESSION_KEY, fromUrl);
  } catch {
    // Storage disabled (private mode) — the current page still works off the
    // URL param; only cross-page persistence is lost.
  }
}
